import { useEffect, useState } from "react";
import { Loader2, Mail, MessageCircle, MessageSquareHeart, Star } from "lucide-react";
import dayjs from "dayjs";
import useApiMethods from "../../../lib/useApiMethods";
import ToastMsg from "../../common/ToastMsg";
import { LEAD_DEMO_FEEDBACK_URL } from "../../../constants/api";
import { cn } from "../../../utils/tailwindUtils";

// Stages after a demo, where asking for feedback makes sense.
const AFTER_DEMO = ['Demo Done', 'Proposal', 'Negotiation', 'Closed Won', 'Closed Lost', 'On Hold', 'Onboarded'];
const CHANNEL = { email: 'Email', whatsapp: 'WhatsApp' };
const STATUS = { pending: 'waiting', sending: 'sending', sent: 'sent', skipped: 'not sent', failed: 'failed' };

function Stars({ value, label }) {
    return (
        <span className="inline-flex items-center gap-0.5" aria-label={`${label} ${value} of 5`}>
            {[1, 2, 3, 4, 5].map(n => (
                <Star key={n} aria-hidden="true"
                      className={cn("h-3.5 w-3.5", n <= value ? "fill-amber-400 text-amber-400" : "text-gray-300 dark:text-gray-600")} />
            ))}
        </span>
    );
}

function Row({ label, children }) {
    return (
        <div className="flex gap-2 text-xs">
            <span className="w-20 shrink-0 text-gray-500 dark:text-gray-400">{label}</span>
            <span className="text-gray-800 dark:text-gray-100 min-w-0">{children}</span>
        </div>
    );
}

/**
 * What the lead said about their demo (email_integration/demo_feedback.py),
 * and the button to ask for it. Shown once a lead is past its demo, or as
 * soon as anything was asked or answered.
 */
export default function DemoFeedbackCard({ customerId, stage }) {
    const { fetch, create } = useApiMethods();
    const [data, setData] = useState(null);
    const [sending, setSending] = useState('');

    useEffect(() => {
        let live = true;
        if (!customerId) return undefined;
        fetch(LEAD_DEMO_FEEDBACK_URL(customerId)).then(res => {
            if (live && res?.status === "success") setData(res.data);
        });
        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId, stage]);

    if (!data) return null;
    // Missing lists count as empty: a partial or older response must hide the
    // card, not crash the whole lead page.
    const fb = data.feedback ? {
        ...data.feedback,
        liked: data.feedback.liked || [],
        labels: { ...data.feedback.labels, concerns: data.feedback.labels?.concerns || [] },
    } : null;
    const requests = Array.isArray(data.requests) ? data.requests : [];
    const channels = Object.keys(CHANNEL).filter(c => data.can_send?.[c]);
    if (!fb && !requests.length && !(AFTER_DEMO.includes(stage) && channels.length)) return null;

    const send = async (channel) => {
        setSending(channel);
        const res = await create(LEAD_DEMO_FEEDBACK_URL(customerId), { channels: [channel] });
        setSending('');
        if (res?.status === "success") {
            setData(res.data);
            ToastMsg("success", res.message || "Feedback request queued");
        } else {
            ToastMsg("error", res?.message || "Could not send the feedback request");
        }
    };

    return (
        <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-900/20 p-4">
            <div className="flex items-center gap-1.5 mb-2">
                <MessageSquareHeart className="h-3.5 w-3.5 text-amber-700 dark:text-amber-300" aria-hidden="true" />
                <p className="text-[10px] font-bold text-amber-700 dark:text-amber-300 uppercase tracking-widest flex-1">
                    Demo feedback
                </p>
            </div>

            {fb ? (
                <div className="space-y-1.5">
                    <Row label="Demo"><Stars value={fb.rating} label="Demo rated" /></Row>
                    {fb.presenter_rating && <Row label="Presenter"><Stars value={fb.presenter_rating} label="Presenter rated" /></Row>}
                    {fb.labels.fit && <Row label="Fit">{fb.labels.fit}</Row>}
                    {fb.labels.next_step && <Row label="Next step"><b>{fb.labels.next_step}</b></Row>}
                    {fb.liked.length > 0 && <Row label="Liked">{fb.liked.join(", ")}</Row>}
                    {fb.labels.concerns.length > 0 && <Row label="Concerns">{fb.labels.concerns.join(", ")}</Row>}
                    {data.presenter && <Row label="Demo by">{data.presenter}</Row>}
                    {fb.comments && <p className="text-xs text-gray-700 dark:text-gray-200 italic whitespace-pre-wrap">“{fb.comments}”</p>}
                    <p className="text-[10px] text-gray-500 dark:text-gray-400">
                        Given on the website, {dayjs(fb.submitted_at).format("DD MMM YYYY")}
                    </p>
                </div>
            ) : (
                <p className="text-xs text-gray-600 dark:text-gray-300">
                    No feedback from the lead yet.{data.presenter && <> Demo by <b>{data.presenter}</b>.</>}
                </p>
            )}

            {requests.length > 0 && (
                <ul className="mt-2 space-y-0.5">
                    {requests.map(r => (
                        <li key={r.channel} className="text-[11px] text-gray-500 dark:text-gray-400">
                            {CHANNEL[r.channel]} {STATUS[r.status] || r.status}
                            {r.sent_to && ` to ${r.sent_to}`}
                            {r.sent_at && `, ${dayjs(r.sent_at).format("DD MMM, h:mm A")}`}
                            {r.reason && ` · ${r.reason}`}
                        </li>
                    ))}
                </ul>
            )}

            {channels.length > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                    <span className="text-[11px] text-gray-500 dark:text-gray-400 mr-0.5">
                        {requests.some(r => r.status === 'sent') ? 'Ask again:' : 'Ask now:'}
                    </span>
                    {channels.map(c => {
                        const Icon = c === 'email' ? Mail : MessageCircle;
                        return (
                            <button key={c} type="button" disabled={!!sending} onClick={() => send(c)}
                                    className="inline-flex items-center gap-1 rounded-md border border-amber-300 dark:border-amber-700 bg-white dark:bg-gray-800 px-2 py-1 text-[11px] font-medium text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/40 disabled:opacity-60">
                                {sending === c ? <Loader2 className="h-3 w-3 animate-spin" /> : <Icon className="h-3 w-3" />}
                                {CHANNEL[c]}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
